import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import QuotationPreviewModal from '../components/QuotationPreviewModal';
import { 
    FileText, 
    Plus, 
    Search, 
    Filter, 
    RotateCcw, 
    Eye, 
    Pencil, 
    Trash2 
} from 'lucide-react';
import './Quotations.css';
import Layout from '../components/Layout';

const getQuotationStatusColor = (status) => {
    const colors = {
        draft: 'secondary',
        sent: 'info',
        accepted: 'success',
        rejected: 'danger',
        converted: 'primary'
    };
    return colors[status] || 'secondary';
};

const ProjectAssurance = () => {
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [filters, setFilters] = useState({
        status: '',
        fromDate: '',
        toDate: '',
        search: ''
    });
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedQuotationId, setSelectedQuotationId] = useState(null);
    const searchInputRef = useRef(null);

    const { isAdmin } = useAuth();


    useEffect(() => {
        fetchQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, filters]);

    const fetchQuotations = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: 20,
                ...filters
            };

            const response = await api.get('/professional-quotations', { params });
            setQuotations(response.data.quotations);
            setPagination(response.data.pagination);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load project assurance');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const wasSearchInput = name === 'search';
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));

        if (wasSearchInput && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 0);
        }
    };

    const resetFilters = () => {
        setFilters({
            status: '',
            fromDate: '',
            toDate: '',
            search: ''
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this project assurance?')) {
            return;
        }

        try {
            await api.delete(`/professional-quotations/${id}`);
            setSuccess('Project assurance deleted successfully');
            fetchQuotations();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete project assurance');
        }
    };

    const handleEditClick = (quotationId) => {
        setSelectedQuotationId(quotationId);
        setShowPreviewModal(true);
    };

    const handleModalClose = (saved) => {
        setShowPreviewModal(false);
        setSelectedQuotationId(null);
        if (saved) {
            fetchQuotations();
        }
    };

    // if (loading && quotations.length === 0) return <Layout><Loading /></Layout>;

    return (
        <div>
            {showPreviewModal && selectedQuotationId && (
                <QuotationPreviewModal
                    quotationId={selectedQuotationId}
                    onClose={handleModalClose}
                />
            )}

            <div className="page-header">
                <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FileText size={28} /> Project Assurance
                </h1>
                {true && (
                    <Link
                        to="/professional-quotations/create"
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        <Plus size={18} /> Create Project Assurance
                    </Link>
                )}
            </div>

            {error && <Alert type="error" message={error} onClose={() => setError('')} />}
            {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

            {/* Filters */}
            <div className="card">
                <div className="card-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Filter size={18} /> Search & Filters
                    </div>
                </div>

                {/* Search Bar */}
                <div style={{ padding: '15px 20px', borderBottom: '1px solid #e5e7eb' }}>
                    <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input
                            ref={searchInputRef}
                            type="text"
                            name="search"
                            className="form-control"
                            placeholder="Search by quotation number, client name, or project name..."
                            value={filters.search}
                            onChange={handleFilterChange}
                            style={{ fontSize: '15px', paddingLeft: '40px' }}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Status</label>
                        <select name="status" className="form-control" value={filters.status} onChange={handleFilterChange}>
                            <option value="">All</option>
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">From Date</label>
                        <input
                            type="date"
                            name="fromDate"
                            className="form-control"
                            value={filters.fromDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">To Date</label>
                        <input
                            type="date"
                            name="toDate"
                            className="form-control"
                            value={filters.toDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button 
                            className="btn btn-secondary" 
                            onClick={resetFilters}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <RotateCcw size={18} /> Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Project Assurance Table */}
            <div className="card">
                <div className="card-header">
                    All Project Assurance ({pagination.total})
                </div>
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Quotation No</th>
                                <th>Date</th>
                                <th>Project Name</th>
                                <th>Client Name</th>
                                <th>Project Type</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotations.length > 0 ? (
                                quotations.map(quotation => (
                                    <tr key={quotation._id}>
                                        <td>{quotation.quotationNumber}</td>
                                        <td>{formatDate(quotation.quotationDate)}</td>
                                        <td><strong>{quotation.projectDetails?.projectName || 'N/A'}</strong></td>
                                        <td>{quotation.clientDetails?.name || 'N/A'}</td>
                                        <td>{quotation.projectDetails?.projectType || 'N/A'}</td>
                                        <td>
                                            <span className={`badge badge-${getQuotationStatusColor(quotation.status)}`}>
                                                {quotation.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Link
                                                    to={`/professional-quotations/${quotation._id}`}
                                                    className="btn btn-primary btn-small"
                                                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                >
                                                    <Eye size={14} /> View
                                                </Link>
                                                {quotation.status !== 'converted' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleEditClick(quotation._id)}
                                                            className="btn btn-secondary btn-small"
                                                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            <Pencil size={14} /> Edit
                                                        </button>
                                                        <button
                                                            className="btn btn-danger btn-small"
                                                            onClick={() => handleDelete(quotation._id)}
                                                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            <Trash2 size={14} /> Delete
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                                        No project assurance found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.pages > 1 && (
                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                />
            )}
        </div>
    );
};

export default ProjectAssurance;
